import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/context/LanguageContext";
import { useConsent } from "@/context/ConsentContext";
import { useUI } from "@/context/UIContext";

function PreferencesDialog({ open, onClose }) {
  const { t, lang } = useLanguage();
  const { consent, save } = useConsent();
  const c = t.cookie;
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (open) {
      setAnalytics(!!(consent && consent.analytics));
      setMarketing(!!(consent && consent.marketing));
    }
  }, [open, consent]);

  const Row = ({ title, desc, checked, onChange, locked, testId }) => (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{desc}</p>
      </div>
      {locked ? (
        <span className="text-xs text-[#C9A96E] whitespace-nowrap pt-0.5">{c.always}</span>
      ) : (
        <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        className="max-w-md bg-[#0B0B0F] border border-[rgba(201,169,110,0.24)] text-foreground"
        data-testid="cookie-preferences-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground">{c.prefTitle}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">{c.prefIntro}</DialogDescription>
        </DialogHeader>
        <div className="divide-y divide-[rgba(201,169,110,0.14)]">
          <Row title={c.essential} desc={c.essentialDesc} locked />
          <Row
            title={c.analytics}
            desc={c.analyticsDesc}
            checked={analytics}
            onChange={setAnalytics}
            testId="cookie-toggle-analytics"
          />
          <Row
            title={c.marketing}
            desc={c.marketingDesc}
            checked={marketing}
            onChange={setMarketing}
            testId="cookie-toggle-marketing"
          />
        </div>
        <Separator className="bg-[rgba(201,169,110,0.14)]" />
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-[rgba(201,169,110,0.4)] text-foreground hover:bg-[rgba(201,169,110,0.1)]"
            onClick={() => {
              save({ analytics: false, marketing: false }, lang);
              onClose();
            }}
            data-testid="cookie-reject-all-pref"
          >
            {c.rejectAll}
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              save({ analytics, marketing }, lang);
              onClose();
            }}
            data-testid="cookie-save-preferences"
          >
            {c.save}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CookieConsent() {
  const { t, lang } = useLanguage();
  const { ready, decided, acceptAll, rejectAll } = useConsent();
  const { cookieSettingsOpen, openCookieSettings, closeCookieSettings } = useUI();
  const c = t.cookie;

  const showBanner = ready && !decided;

  return (
    <>
      {showBanner ? (
        <div
          className="fixed bottom-4 left-4 right-4 sm:right-auto z-50"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="sm:max-w-sm rounded-xl bg-[rgba(15,16,20,0.92)] border border-[rgba(201,169,110,0.22)] backdrop-blur-md shadow-[0_10px_36px_rgba(0,0,0,0.45)] px-4 py-3.5">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {c.body}{" "}
              <button
                className="underline underline-offset-2 hover:text-foreground transition-colors"
                onClick={openCookieSettings}
                data-testid="cookie-manage"
              >
                {c.manage}
              </button>
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                className="h-8 px-4 text-xs"
                onClick={() => acceptAll(lang)}
                data-testid="cookie-accept-all"
              >
                {c.acceptAll}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => rejectAll(lang)}
                data-testid="cookie-reject-all"
              >
                {c.rejectAll}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <PreferencesDialog open={cookieSettingsOpen} onClose={closeCookieSettings} />
    </>
  );
}
